/**
 * useViewportTier — the viewport class the responsive operator layouts branch on
 * (PROD-UX-077 / FULL-0163).
 *
 * Three tiers, aligned with the Tailwind breakpoints the surfaces already use:
 *
 *   mobile  — 0–639px  (< sm):  stacked cards for wide tables, filters behind a disclosure
 *   tablet  — 640–1023px (sm, < lg): the filter rail stacks; tables scroll horizontally
 *   desktop — 1024px+  (lg+):   side-by-side queue + filters, full table
 *
 * The width is read from `window.innerWidth`, the same browser signal `Layout` already uses for
 * its rail. A render without a numeric `innerWidth` (non-DOM, older embedding) reads as desktop,
 * the layout that shows every column and control. The hook chooses presentation only: it never
 * gates data, permission, approval or an action, and no tier hides a fact the desktop view keeps
 * (mobile tables keep every cell as a labelled card).
 */
import { useEffect, useState } from "react";

export const VIEWPORT_TIERS = Object.freeze({
  mobile: "mobile",
  tablet: "tablet",
  desktop: "desktop",
});

/** Inclusive upper bounds, matching `frontend/src/styles/responsive.css`. */
export const MOBILE_MAX_WIDTH = 639;
export const TABLET_MIN_WIDTH = 640;
export const DESKTOP_MIN_WIDTH = 1024;

/**
 * The tier for an explicit width. Non-finite or unknown values fail to `desktop`, which no
 * responsive rule removes content from.
 */
export function viewportTierForWidth(width) {
  if (typeof width !== "number" || !Number.isFinite(width)) return VIEWPORT_TIERS.desktop;
  if (width <= MOBILE_MAX_WIDTH) return VIEWPORT_TIERS.mobile;
  if (width < DESKTOP_MIN_WIDTH) return VIEWPORT_TIERS.tablet;
  return VIEWPORT_TIERS.desktop;
}

/** The current browser width's tier, read at call time. */
export function viewportTierNow() {
  if (typeof window === "undefined") return VIEWPORT_TIERS.desktop;
  return viewportTierForWidth(window.innerWidth);
}

/** The live tier; recomputed on every resize. */
export function useViewportTier() {
  const [tier, setTier] = useState(viewportTierNow);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setTier(viewportTierNow());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return tier;
}
